import React from 'react'
import { CheckCircle2, Clock, Play, Lock, Star } from 'lucide-react'

// --- Module 2: role play card status derivation (shared by coach screen & dashboard) ---
// No new data: derived from existing card fields so the coach screen and the user
// dashboard always agree on a role play's status/mastery.
export type M2CardStatus = 'mastered' | 'progress' | 'started' | 'notStarted'

export const deriveCardStatus = (card: { sessionsPlayed: number; targetSessions: number; totalScore: number; targetScore: number }): M2CardStatus => {
  if (card.sessionsPlayed === 0) return 'notStarted'
  const avgStars = card.totalScore / card.sessionsPlayed
  const avgPercent = (avgStars / 5) * 100
  const sessionsMet = card.targetSessions > 0 ? card.sessionsPlayed >= card.targetSessions : true
  const scoreMet = card.targetScore > 0 ? avgPercent >= card.targetScore : true
  if (sessionsMet && scoreMet) return 'mastered'
  if (card.sessionsPlayed >= 2) return 'progress'
  return 'started'
}

export const M2_STATUS_CONFIG: Record<M2CardStatus, { label: string; icon: React.ReactNode; pill: React.CSSProperties }> = {
  mastered: { label: 'Mastered', icon: <CheckCircle2 size={10} />, pill: { background: 'var(--m2-mint)', color: 'var(--m2-brand)' } },
  progress: { label: 'In progress', icon: <Clock size={10} />, pill: { background: 'var(--m2-amber-bg)', color: 'var(--m2-amber-ink)' } },
  started: { label: 'Started', icon: <Play size={10} />, pill: { background: 'var(--m2-lime)', color: 'var(--m2-brand-dark)' } },
  notStarted: { label: 'Not started', icon: <Lock size={10} />, pill: { background: 'var(--m2-line-soft)', color: 'var(--m2-muted)' } },
}

// --- Shared role play card pieces (coach screen cards column + user dashboard) ---

export const M2StatusPill: React.FC<{ status: M2CardStatus }> = ({ status }) => {
  const cfg = M2_STATUS_CONFIG[status]
  return (
    <span className="inline-flex items-center gap-1 text-[9.5px] font-bold tracking-[0.9px] uppercase px-2 py-[3px] rounded-full shrink-0" style={cfg.pill}>
      {cfg.icon}
      {cfg.label}
    </span>
  )
}

/** Card footer: sessions vs target, star rating (score only once played), and a session progress bar once started. */
export const M2CardFoot: React.FC<{ sessionsPlayed: number; targetSessions: number; avgStars: number }> = ({ sessionsPlayed, targetSessions, avgStars }) => {
  const played = sessionsPlayed > 0
  const starsRating = Math.round(avgStars)
  const sessionPct = targetSessions > 0 ? Math.min(100, Math.round((sessionsPlayed / targetSessions) * 100)) : 0
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium" style={{ color: 'var(--m2-muted)' }}>
          Sessions{' '}
          <strong className="font-bold" style={{ color: 'var(--m2-ink)' }}>
            {sessionsPlayed} / {targetSessions || '∞'}
          </strong>
        </span>
        <span className="flex items-center gap-px">
          {[1, 2, 3, 4, 5].map((star) => {
            const lit = played && star <= starsRating
            return <Star key={star} size={10} style={{ color: lit ? 'var(--m2-amber)' : '#DDE4DE', fill: lit ? 'var(--m2-amber)' : '#DDE4DE' }} />
          })}
          {played && (
            <span className="text-[11px] font-bold ml-1" style={{ color: 'var(--m2-ink)' }}>
              {avgStars.toFixed(1)}
            </span>
          )}
        </span>
      </div>
      {played && (
        <div className="w-[70px] h-[3px] rounded-sm overflow-hidden mt-[7px]" style={{ background: '#EEF2EC' }}>
          <div className="h-full rounded-sm" style={{ width: `${sessionPct}%`, background: 'var(--m2-lime-hover)' }} />
        </div>
      )}
    </>
  )
}

// Mastery % consistent with the card grid: 100 once `deriveCardStatus` reports mastered,
// otherwise the blend of session- and score-progress toward the target gates.
export const deriveMasteryPct = (card: { sessionsPlayed: number; targetSessions: number; totalScore: number; targetScore: number }): number => {
  if (card.sessionsPlayed === 0) return 0
  if (deriveCardStatus(card) === 'mastered') return 100
  const sessionProgress = card.targetSessions > 0 ? Math.min(100, (card.sessionsPlayed / card.targetSessions) * 100) : 100
  const avgPercent = (card.totalScore / card.sessionsPlayed / 5) * 100
  const scoreProgress = card.targetScore > 0 ? Math.min(100, (avgPercent / card.targetScore) * 100) : 100
  return Math.round((sessionProgress + scoreProgress) / 2)
}

// Display title for a role play card. Target payload names arrive as e.g. "#3: The Right to Ask - RP".
// Cards are deliberately unnumbered: a visible number (positional or authored) was quoted between
// customers and learners and caused miscommunication because card order is not stable. "RP" is
// implied by the Role plays tab. Both are stripped for display only (the raw name is still what
// gets persisted with reviews). Do not reintroduce a number badge to "restore" the prefix.
export const displayRolePlayTitle = (name: string | null | undefined): string => {
  if (!name) return ''
  return (
    name
      // "#3 Title", "# 1 - Title", "#1:Title", "1. Title" — a bare number needs a separator so "10 Ways" survives
      .replace(/^\s*(?:#\s*\d+\s*[:.\-–—·]?|\d+\s*[:.\-–—·])\s*/, '')
      // " - RP", " -  RP", " - Role Play"
      .replace(/\s*[-–—:·]\s*(?:RP|Role\s*-?\s*Play)\s*$/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim()
  )
}
